
import { Message, MessageTone } from "@/types/message";
import MessageCardHeader from "./MessageCardHeader";
import MessageCardContent from "./MessageCardContent";
import MessageCardFooter from "./MessageCardFooter";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef } from "react";
import { BadgeInfo, History } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface MessageCardProps {
  message: Message;
  companyName: string;
  isLatestMessage?: boolean;
  isLatestAIMessage?: boolean;
  onRegenerateAIResponse?: (messageId: string, tone: MessageTone, customInstructions?: string) => Promise<Message | null>;
  isRegenerating?: boolean;
  orderNumber?: string;
  userEmail?: string;
  sellerEmail?: string;
  amount?: number;
  id?: string;
  showVersionInfo?: boolean;
  versionNumber?: number;
  totalVersions?: number;
}

const MessageCard = ({ 
  message, 
  companyName, 
  isLatestMessage = false,
  isLatestAIMessage = false,
  onRegenerateAIResponse,
  isRegenerating = false,
  orderNumber = "",
  userEmail = "",
  sellerEmail = "",
  amount = 0,
  id,
  showVersionInfo = false,
  versionNumber,
  totalVersions
}: MessageCardProps) => {
  const { toast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Different border colors based on message type
  const borderColorClass = {
    user: "border-l-blue-500",
    seller: "border-l-orange-500",
    ai: "border-l-green-500",
    system: "border-l-gray-500"
  }[message.type];
  
  // Apply highlight animation to newly generated AI messages
  const highlightClass = isLatestMessage && message.type === "ai" ? "animate-highlight-pulse ring-1 ring-primary/20" : "";
  
  // Check if this is the initial message (no seller responses before it)
  const isInitialMessage = message.type === "ai" && isLatestAIMessage && 
    (message.metadata?.exchangeCount === 0 || !message.metadata?.exchangeCount);
  
  // Check if message is regenerated
  const isRegenerated = message.isRegenerated || Boolean(message.relatedToMessageId);
  
  // Version indicator text
  const versionInfo = getVersionInfo();
  
  // Generate appropriate version information
  function getVersionInfo() {
    if (!showVersionInfo) return "";
    
    if (versionNumber && totalVersions) {
      if (versionNumber === totalVersions) {
        return `Latest version (v${versionNumber})`;
      }
      return `Version ${versionNumber} of ${totalVersions}`;
    }
    
    if (message.version) {
      return `v${message.version}`;
    }
    
    return isRegenerated ? "Regenerated version" : "";
  }
  
  // Auto-scroll to latest message after render
  useEffect(() => {
    if (isLatestMessage && cardRef.current) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [isLatestMessage, message.id]);
  
  // Determine if this card should have reduced opacity based on version status
  const isOlderVersion = showVersionInfo && versionNumber && totalVersions && versionNumber < totalVersions;
  const opacityClass = (isRegenerated && !isLatestAIMessage) || isOlderVersion ? "opacity-70" : "";
  
  return (
    <div className="relative">
      <Card 
        className={cn(
          "border-l-4 mb-4 transition-all", 
          borderColorClass,
          highlightClass,
          isRegenerating ? "opacity-50" : "",
          isLatestMessage ? "shadow-md" : "shadow-sm",
          opacityClass
        )}
        id={id || `message-${message.id}`}
        data-message-id={message.id}
        ref={cardRef}
      >
        <MessageCardHeader 
          message={message} 
          companyName={companyName}
        />
        
        {/* Show version information if available */}
        {versionInfo && (
          <div className="px-4 py-1 flex items-center text-xs text-muted-foreground">
            <History className="w-3 h-3 mr-1" />
            <span>{versionInfo}</span>
            
            {/* Add "Latest" badge for the latest version */}
            {versionNumber && totalVersions && versionNumber === totalVersions && (
              <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200 text-[10px]">
                Latest
              </Badge>
            )}
          </div>
        )}
        
        <MessageCardContent 
          content={message.content}
          isLatestMessage={isLatestMessage}
          isLatestAIMessage={isLatestAIMessage}
          metadata={message.metadata}
          amount={amount}
        />
        
        {message.type === "ai" && isLatestAIMessage && (
          <MessageCardFooter 
            isAIMessage={message.type === "ai"}
            isLatestAIMessage={isLatestAIMessage}
            isInitialMessage={isInitialMessage}
            messageId={message.id}
            onRegenerateAIResponse={onRegenerateAIResponse}
            isRegenerating={isRegenerating}
            suggestedTone={message.metadata?.suggestedTone}
            content={message.content}
            orderNumber={orderNumber}
            userEmail={userEmail}
            sellerEmail={sellerEmail}
            customInstructions={message.metadata?.customInstructions}
          />
        )}
      </Card>
    </div>
  );
};

export default MessageCard;
